import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { Switch } from 'components/UI/V3/Forms/Switch/Switch';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import DescriptionText from 'components/UI/V3/DescriptionText/DescriptionText';
import { AlertMessage } from 'components/UI/V3/AlertMessage/AlertMessage';
import { ChipSelector, Element } from 'components/Service/ChipSelector';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import Service from 'model/Service';
import {
    buildSelectableDaycareRoomElements,
    buildSelectedDaycareRoomElements,
    flattenDaycareRoomUnits
} from './roomElements';
import styles from './RoomsAndSpacesSection.module.scss';

interface RoomsAndSpacesSectionProps {
    className?: string;
    enabled: boolean;
    marketplaces: Service['marketplaces'];
    selectedPetTypeIds: Array<string>;
    onEnabledChange: (enabled: boolean) => void;
    onChangeMarketplaces: (marketplaces: Service['marketplaces']) => void;
}

export const RoomsAndSpacesSection: FC<RoomsAndSpacesSectionProps> = ({
    enabled,
    marketplaces,
    selectedPetTypeIds,
    onEnabledChange,
    onChangeMarketplaces,
    className
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const [expandedMarketplaceIds, setExpandedMarketplaceIds] = useState<Array<number>>([]);
    const hasAutoExpandedRef = useRef(false);

    const selectedMarketplaces = useMemo(
        () =>
            marketplaces
                .map(serviceMarketplace => {
                    const servicesInfo = organizationServicesInfo.find(
                        info => info.marketplace.id === serviceMarketplace.marketplaceId
                    );

                    if (!servicesInfo) {
                        return null;
                    }

                    return {
                        serviceMarketplace,
                        servicesInfo
                    };
                })
                .filter(
                    (
                        marketplace
                    ): marketplace is {
                        serviceMarketplace: Service['marketplaces'][number];
                        servicesInfo: (typeof organizationServicesInfo)[number];
                    } => marketplace !== null
                )
                .sort((left, right) =>
                    left.servicesInfo.marketplace.name
                        .toLocaleLowerCase()
                        .localeCompare(right.servicesInfo.marketplace.name.toLocaleLowerCase())
                ),
        [organizationServicesInfo, marketplaces]
    );

    useEffect(() => {
        if (!enabled || hasAutoExpandedRef.current || selectedMarketplaces.length === 0) {
            return;
        }

        setExpandedMarketplaceIds(
            selectedMarketplaces.map(({ serviceMarketplace }) => serviceMarketplace.marketplaceId)
        );
        hasAutoExpandedRef.current = true;
    }, [enabled, selectedMarketplaces]);

    const hasAnyDaycareRooms = selectedMarketplaces.some(
        ({ servicesInfo }) => (servicesInfo.rooms?.daycare?.length ?? 0) > 0
    );

    const marketplaceRoomSelectionChangeHandler =
        (marketplaceId: number) =>
        (elements: Array<Element<number>>): void => {
            onChangeMarketplaces(
                marketplaces.map(marketplace =>
                    marketplace.marketplaceId === marketplaceId
                        ? { ...marketplace, roomUnitIds: elements.map(element => element.id), roomIds: [] }
                        : marketplace
                )
            );
        };

    const toggleMarketplaceExpanded = (marketplaceId: number) => {
        setExpandedMarketplaceIds(prev =>
            prev.includes(marketplaceId) ? prev.filter(id => id !== marketplaceId) : [...prev, marketplaceId]
        );
    };

    return (
        <div className={`${styles.switchSection} ${className || ''}`}>
            <Label className={styles.roomsLabelSwitch}>
                Rooms & Spaces
                <Switch
                    checked={enabled}
                    onChange={onEnabledChange}
                />
            </Label>
            <DescriptionText text="Choose the applicable room units for this service." />

            {enabled && (
                <div className={styles.roomsContainer}>
                    {!hasAnyDaycareRooms && (
                        <div className={styles.noRoomsAvailable}>
                            <AlertMessage
                                icon={faInfoCircle}
                                iconColor="#BAA997"
                                bdColor="#BCB8AE"
                                bgColor="#F8F5F1"
                                className={styles.alert}
                            >
                                <span>
                                    You don’t have any daycare rooms set up yet. Please{' '}
                                    <a href="/business_settings/info/rooms">add a daycare room</a> before completing
                                    your setup
                                </span>
                            </AlertMessage>
                        </div>
                    )}

                    {hasAnyDaycareRooms && (
                        <>
                            <p className={styles.roomsSectionTitle}>
                                Choose the applicable room units for each location
                            </p>
                            {selectedMarketplaces.map(({ serviceMarketplace, servicesInfo }) => {
                                const daycareRooms = servicesInfo.rooms?.daycare ?? [];
                                const allUnits = flattenDaycareRoomUnits(daycareRooms);
                                const selectableElements = buildSelectableDaycareRoomElements(
                                    daycareRooms,
                                    selectedPetTypeIds
                                );
                                const selectedElements = buildSelectedDaycareRoomElements(
                                    daycareRooms,
                                    serviceMarketplace.roomUnitIds ?? []
                                );
                                const selectedCount = selectedElements.length;
                                const totalCount = selectableElements.length;
                                const isExpanded = expandedMarketplaceIds.includes(serviceMarketplace.marketplaceId);

                                return (
                                    <div
                                        key={serviceMarketplace.marketplaceId}
                                        className={styles.marketplaceRoomsSection}
                                    >
                                        <div
                                            className={styles.marketplaceHeader}
                                            onClick={() => toggleMarketplaceExpanded(serviceMarketplace.marketplaceId)}
                                        >
                                            <div className={styles.marketplaceHeaderLeft}>
                                                <p className={styles.marketplaceName}>
                                                    {servicesInfo.marketplace.name}
                                                </p>
                                                <p className={styles.marketplaceCount}>
                                                    ({selectedCount}/{totalCount})
                                                </p>
                                            </div>
                                            <FontAwesomeIcon
                                                icon={isExpanded ? faChevronUp : faChevronDown}
                                                className={styles.marketplaceChevron}
                                            />
                                        </div>
                                        <div className={styles.marketplaceNameDivider} />
                                        {isExpanded && (
                                            <div className={styles.marketplaceRoomsContent}>
                                                {allUnits.length === 0 ? (
                                                    <p className={styles.roomsNotDefined}>Rooms not defined</p>
                                                ) : selectableElements.length === 0 ? (
                                                    <p className={styles.roomsNotDefined}>
                                                        No room units match the selected pet types
                                                    </p>
                                                ) : (
                                                    <ChipSelector
                                                        id={`daycare_details_rooms_selector_${serviceMarketplace.marketplaceId}`}
                                                        searchLabel="Search rooms"
                                                        elements={selectableElements}
                                                        selectedElements={selectedElements}
                                                        changeSelection={marketplaceRoomSelectionChangeHandler(
                                                            serviceMarketplace.marketplaceId
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
